import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  InputBase,
  Grid,
  Paper,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import WindowIcon from '@mui/icons-material/Window';
import WebIcon from '@mui/icons-material/Web';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const HelpCenter = () => {
  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: '#f5f5f5',
          pt: 8,
          pb: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: '60%', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                color: '#000',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              Help Center
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#333',
                mb: 4,
                fontSize: '1.1rem',
                lineHeight: 1.6
              }}
            >
              We're here to help you with any questions and provide the guidance you need to bring your idea to life. When you're ready to go online, we're here, prepared and trained to guide you from start to success.
            </Typography>

            {/* Search Bar */}
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <InputBase
                placeholder="How can we help you?"
                sx={{ 
                  flex: 1,
                  p: 2,
                  fontSize: '1rem',
                  '&::placeholder': {
                    color: '#666',
                    opacity: 1
                  }
                }}
              />
              <Box 
                sx={{ 
                  p: 1,
                  bgcolor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  cursor: 'pointer'
                }}
              >
                <SearchIcon sx={{ color: '#fff' }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Categories Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <SettingsIcon sx={{ fontSize: 40, color: '#000', mb: 2 }} />
                <Box sx={{ 
                  width: 40, 
                  height: 2, 
                  bgcolor: '#0073EC', 
                  mx: 'auto', 
                  mb: 2 
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Account Management
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <LanguageIcon sx={{ fontSize: 40, color: '#000', mb: 2 }} />
                <Box sx={{ 
                  width: 40, 
                  height: 2, 
                  bgcolor: '#0073EC', 
                  mx: 'auto', 
                  mb: 2 
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Domains
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <WindowIcon sx={{ fontSize: 40, color: '#000', mb: 2 }} />
                <Box sx={{ 
                  width: 40, 
                  height: 2, 
                  bgcolor: '#0073EC', 
                  mx: 'auto', 
                  mb: 2 
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Microsoft 365
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  by Larvaram
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <WebIcon sx={{ fontSize: 40, color: '#000', mb: 2 }} />
                <Box sx={{ 
                  width: 40, 
                  height: 2, 
                  bgcolor: '#0073EC', 
                  mx: 'auto', 
                  mb: 2 
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Websites +
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Marketing
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#000', color: '#fff', mt: 6 }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Grid container spacing={4}>
            {/* Quick Links Column */}
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['About Innospherri', 'Pricing Plans', 'Contact Us', 'Smart Search', 'Activate Premium Access'].map((link) => (
                  <Typography
                    key={link}
                    variant="body2"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: '#0073EC' },
                      transition: 'color 0.2s'
                    }}
                  >
                    {link}
                  </Typography>
                ))}
              </Box>
            </Grid>

            {/* Connect Column */}
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Connect
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                <Box
                  component="a"
                  href="#"
                  sx={{
                    color: '#fff',
                    '&:hover': { color: '#0073EC' },
                    transition: 'color 0.2s'
                  }}
                >
                  <FacebookIcon />
                </Box>
                <Box
                  component="a"
                  href="#"
                  sx={{
                    color: '#fff',
                    '&:hover': { color: '#0073EC' },
                    transition: 'color 0.2s'
                  }}
                >
                  <TwitterIcon />
                </Box>
                <Box
                  component="a"
                  href="#"
                  sx={{
                    color: '#fff',
                    '&:hover': { color: '#0073EC' },
                    transition: 'color 0.2s'
                  }}
                >
                  <LinkedInIcon />
                </Box>
              </Box>

              {/* Newsletter Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Newsletter
              </Typography>
              <Box sx={{ 
                display: 'flex',
                gap: 1,
                mb: 2
              }}>
                <InputBase
                  placeholder="Enter your email"
                  sx={{
                    flex: 1,
                    bgcolor: '#fff',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    fontSize: '0.875rem',
                    color: '#000'
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#00A651',
                    '&:hover': { bgcolor: '#008c44' },
                    textTransform: 'none'
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>

            {/* About Column */}
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                About Larvarris AI
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                Larvarris AI is a cutting-edge platform that uses artificial intelligence to diagnose 
                and assist with home device repairs. From AI-powered fault detection to expert video 
                consultations and a secure marketplace for genuine spare parts, we make technical 
                troubleshooting easier, faster, and more reliable.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Us Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
      >
        <Box
          sx={{
            bgcolor: '#fff',
            color: '#000',
            py: 1.5,
            px: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>
            Contact Us
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HelpCenter;
